import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock3,
  Download,
  Eye,
  FileCheck2,
  FileText,
  Filter,
  Link2,
  MailCheck,
  MailQuestion,
  Paperclip,
  Plus,
  ReceiptText,
  RefreshCcw,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  Upload,
  WalletCards,
  X,
} from "lucide-react";

import TitleButtons from "../../../../components/TitleButtons";
import { useTheme } from "../../../../context/ThemeContext";

type SectionKey =
  | "visao-geral"
  | "pendencias"
  | "enviadas"
  | "aguardando-envio"
  | "canceladas";

type TipoNotaFiscal = "servico" | "produto" | "mista";

type TipoReceitaSimples =
  | "reparo_tecnico"
  | "licenciamento_software"
  | "outro_servico";

type SituacaoFiscal =
  | "rascunho"
  | "regular"
  | "aguardando_pdf"
  | "aguardando_xml"
  | "inconsistente"
  | "cancelada"
  | "substituida"
  | "enviada_homologacao"
  | "autorizada_homologacao"
  | "rejeitada_homologacao"
  | "erro_integracao";

type StatusEnvioCliente =
  | "nao_iniciado"
  | "parcial"
  | "concluido"
  | "pendente_validacao"
  | "nao_aplicavel";

type StatusVinculoRecebivel =
  | "sem_recebivel"
  | "recebivel_aberto"
  | "recebivel_vencido"
  | "recebivel_recebido";

type TipoDocumentoFiscal = "pdf" | "xml" | "cancelamento" | "substituicao";

type StatusDocumentoFiscal = "anexado" | "pendente" | "nao_aplicavel";

type DestinoEnvio =
  | "solicitante"
  | "fiscal_cliente"
  | "compras_cliente"
  | "portal_cliente"
  | "financeiro_cliente";

type StatusItemEnvio = "enviado" | "pendente" | "nao_aplicavel";

type DocumentoFiscal = {
  tipo: TipoDocumentoFiscal;
  label: string;
  status: StatusDocumentoFiscal;
  arquivoNome: string | null;
  anexadoEm: string | null;
  responsavel: string | null;
};

type ProcedimentoEnvioItem = {
  id: number;
  destino: DestinoEnvio;
  descricao: string;
  documento: "pdf" | "xml" | "pdf_xml" | "outro";
  contato: string;
  canal: "email" | "portal" | "whatsapp" | "outro";
  status: StatusItemEnvio;
  enviadoEm: string | null;
  responsavel: string | null;
};

type HistoricoNota = {
  id: number;
  data: string;
  titulo: string;
  descricao: string;
  responsavel: string;
};

type NotaFiscalEmitida = {
  id: number;
  numeroNota: string;
  serie: string;
  tipo: TipoNotaFiscal;
  tipoReceitaSimples: TipoReceitaSimples;
  cliente: string;
  cnpjCpfCliente: string;
  municipioPrestacao: string;
  ufPrestacao: string;
  competencia: string;
  dataEmissao: string;
  valorBruto: number;
  situacaoFiscal: SituacaoFiscal;
  statusEnvioCliente: StatusEnvioCliente;
  statusVinculoRecebivel: StatusVinculoRecebivel;
  contaReceberCodigo: string | null;
  contaReceberVencimento: string | null;
  codigoServico: string;
  descricaoServico: string;
  centroCusto: string;
  projeto: string;
  responsavel: string;
  observacao: string | null;
  protocoloPrefeitura?: string | null;
  codigoVerificacao?: string | null;
  documentos: DocumentoFiscal[];
  procedimentoEnvio: ProcedimentoEnvioItem[];
  historico: HistoricoNota[];
};

type FilterState = {
  busca: string;
  situacaoFiscal: string;
  statusEnvioCliente: string;
  statusVinculoRecebivel: string;
  tipoReceitaSimples: string;
  cliente: string;
  competencia: string;
};

type AmbienteNfse = "homologacao" | "producao";

type NovaNfseForm = {
  ambiente: AmbienteNfse;

  clienteNome: string;
  clienteDocumento: string;
  clienteEmail: string;

  municipioPrestacao: string;
  ufPrestacao: string;
  competencia: string;

  codigoServico: string;
  codigoCnae: string;
  descricaoServico: string;
  tipoReceitaSimples: TipoReceitaSimples;

  valorServico: string;
  aliquotaIss: string;

  issRetido: boolean;
  simplesNacional: boolean;
  incentivoFiscal: boolean;

  projeto: string;
  centroCusto: string;
  observacao: string;
};

type EmitirNfsePrefeituraResponse = {
  ok: boolean;
  message?: string;
  nota?: {
    id?: number;
    status?: string;
    ambiente?: AmbienteNfse;
    protocolo_prefeitura?: string | null;
    numero_nfse?: string | null;
    codigo_verificacao?: string | null;
    mensagem_retorno?: string | null;
    xml_retorno?: string | null;
  };
};

type NotasFiscaisEmitidasLayoutProps = {
  section?: SectionKey;
};

const DEPARTMENT_COLOR = "#2f3f59";

const HEADER_HEIGHT = 52;
const LEFTBAR_WIDTH = 64;
const MOBILE_BREAKPOINT = 900;

const DOCUMENTOS_FISCAIS: Array<{
  tipo: TipoDocumentoFiscal;
  label: string;
}> = [
  { tipo: "pdf", label: "PDF da nota" },
  { tipo: "xml", label: "XML da nota" },
  { tipo: "cancelamento", label: "Cancelamento" },
  { tipo: "substituicao", label: "Substituição" },
];

const fixedNotas: NotaFiscalEmitida[] = [
  {
    id: 1,
    numeroNota: "000000124",
    serie: "NFS-e",
    tipo: "servico",
    tipoReceitaSimples: "reparo_tecnico",
    cliente: "Googlemarine Serviços Marítimos Ltda",
    cnpjCpfCliente: "12.345.678/0001-90",
    municipioPrestacao: "Niterói",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-03",
    valorBruto: 12500,
    situacaoFiscal: "regular",
    statusEnvioCliente: "concluido",
    statusVinculoRecebivel: "recebivel_recebido",
    contaReceberCodigo: "CR-2026-0001",
    contaReceberVencimento: "2026-05-18",
    codigoServico: "14.01",
    descricaoServico:
      "Serviço de manutenção, reparo e testes em equipamento eletrônico marítimo.",
    centroCusto: "Laboratório / Reparos",
    projeto: "Case GM-2026-014",
    responsavel: "Financeiro",
    observacao:
      "Cliente exige envio do PDF ao solicitante e XML ao fiscal cadastrado.",
    protocoloPrefeitura: "PRT-2026-000124",
    codigoVerificacao: "GM124",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0001.pdf",
        anexadoEm: "2026-05-03",
        responsavel: "Financeiro",
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0001.xml",
        anexadoEm: "2026-05-03",
        responsavel: "Financeiro",
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [
      {
        id: 1,
        destino: "solicitante",
        descricao: "Enviar PDF da nota ao solicitante do serviço.",
        documento: "pdf",
        contato: "solicitante@googlemarine.com",
        canal: "email",
        status: "enviado",
        enviadoEm: "2026-05-03",
        responsavel: "Financeiro",
      },
      {
        id: 2,
        destino: "fiscal_cliente",
        descricao: "Enviar XML da nota para o departamento fiscal do cliente.",
        documento: "xml",
        contato: "fiscal@googlemarine.com",
        canal: "email",
        status: "enviado",
        enviadoEm: "2026-05-03",
        responsavel: "Financeiro",
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-03",
        titulo: "Nota emitida",
        descricao: "NFS-e emitida e documentos fiscais anexados.",
        responsavel: "Financeiro",
      },
      {
        id: 2,
        data: "2026-05-03",
        titulo: "Envio concluído",
        descricao: "PDF enviado ao solicitante e XML enviado ao fiscal.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 2,
    numeroNota: "000000125",
    serie: "NFS-e",
    tipo: "servico",
    tipoReceitaSimples: "reparo_tecnico",
    cliente: "Bravante Navegação S.A.",
    cnpjCpfCliente: "33.333.333/0001-33",
    municipioPrestacao: "Rio de Janeiro",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-05",
    valorBruto: 18750,
    situacaoFiscal: "regular",
    statusEnvioCliente: "parcial",
    statusVinculoRecebivel: "recebivel_aberto",
    contaReceberCodigo: "CR-2026-0002",
    contaReceberVencimento: "2026-05-20",
    codigoServico: "14.01",
    descricaoServico:
      "Reparo técnico, testes funcionais e emissão de relatório para equipamento de comunicação.",
    centroCusto: "Operações / Marítimo",
    projeto: "Case BV-2026-021",
    responsavel: "Financeiro",
    observacao:
      "Procedimento do cliente exige envio pelo portal e cópia para o fiscal.",
    protocoloPrefeitura: "PRT-2026-000125",
    codigoVerificacao: "BV125",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0002.pdf",
        anexadoEm: "2026-05-05",
        responsavel: "Financeiro",
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0002.xml",
        anexadoEm: "2026-05-05",
        responsavel: "Financeiro",
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [
      {
        id: 1,
        destino: "portal_cliente",
        descricao: "Lançar PDF e XML no portal de fornecedores do cliente.",
        documento: "pdf_xml",
        contato: "Portal Bravante",
        canal: "portal",
        status: "enviado",
        enviadoEm: "2026-05-05",
        responsavel: "Financeiro",
      },
      {
        id: 2,
        destino: "fiscal_cliente",
        descricao: "Enviar cópia do XML para o e-mail fiscal.",
        documento: "xml",
        contato: "fiscal@bravante.com.br",
        canal: "email",
        status: "pendente",
        enviadoEm: null,
        responsavel: null,
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-05",
        titulo: "Nota emitida",
        descricao: "NFS-e emitida com PDF e XML anexados.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 3,
    numeroNota: "RASCUNHO",
    serie: "NFS-e",
    tipo: "servico",
    tipoReceitaSimples: "reparo_tecnico",
    cliente: "Vision Marine Electronics",
    cnpjCpfCliente: "Não informado",
    municipioPrestacao: "Niterói",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-09",
    valorBruto: 0,
    situacaoFiscal: "rascunho",
    statusEnvioCliente: "nao_aplicavel",
    statusVinculoRecebivel: "sem_recebivel",
    contaReceberCodigo: null,
    contaReceberVencimento: null,
    codigoServico: "14.01",
    descricaoServico:
      "Rascunho de nota para serviço técnico ainda em validação comercial.",
    centroCusto: "Comercial / Propostas",
    projeto: "Proposta VM-2026-003",
    responsavel: "Financeiro",
    observacao:
      "Aguardando dados cadastrais completos e fechamento do valor final.",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [],
    historico: [
      {
        id: 1,
        data: "2026-05-09",
        titulo: "Rascunho criado",
        descricao: "Nota em preparação aguardando dados do cliente.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 4,
    numeroNota: "000000128",
    serie: "NFS-e",
    tipo: "servico",
    tipoReceitaSimples: "reparo_tecnico",
    cliente: "Cliente Teste Cancelamento",
    cnpjCpfCliente: "00.000.000/0001-00",
    municipioPrestacao: "Niterói",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-09",
    valorBruto: 4500,
    situacaoFiscal: "cancelada",
    statusEnvioCliente: "nao_aplicavel",
    statusVinculoRecebivel: "sem_recebivel",
    contaReceberCodigo: null,
    contaReceberVencimento: null,
    codigoServico: "14.01",
    descricaoServico:
      "Nota cancelada por erro nos dados cadastrais informados pelo tomador.",
    centroCusto: "Financeiro",
    projeto: "Cancelamento interno",
    responsavel: "Financeiro",
    observacao:
      "Emitir nova nota após correção dos dados cadastrais do tomador.",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0006-cancelada.pdf",
        anexadoEm: "2026-05-09",
        responsavel: "Financeiro",
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0006-cancelada.xml",
        anexadoEm: "2026-05-09",
        responsavel: "Financeiro",
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "anexado",
        arquivoNome: "NF-2026-0006-cancelamento.pdf",
        anexadoEm: "2026-05-09",
        responsavel: "Financeiro",
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [],
    historico: [
      {
        id: 1,
        data: "2026-05-09",
        titulo: "Nota cancelada",
        descricao: "Cancelamento realizado por erro cadastral do tomador.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 5,
    numeroNota: "000000129",
    serie: "NFS-e",
    tipo: "servico",
    tipoReceitaSimples: "licenciamento_software",
    cliente: "Cliente Logvi Junho",
    cnpjCpfCliente: "66.666.666/0001-66",
    municipioPrestacao: "Niterói",
    ufPrestacao: "RJ",
    competencia: "2026-06",
    dataEmissao: "2026-06-03",
    valorBruto: 7500,
    situacaoFiscal: "regular",
    statusEnvioCliente: "concluido",
    statusVinculoRecebivel: "recebivel_aberto",
    contaReceberCodigo: "CR-2026-0004",
    contaReceberVencimento: "2026-06-20",
    codigoServico: "1.05",
    descricaoServico:
      "Licenciamento mensal de módulo Logvi com suporte operacional.",
    centroCusto: "Tecnologia / Logvi",
    projeto: "Logvi SaaS",
    responsavel: "Financeiro",
    observacao: "Competência de junho usada para testar apuração mensal.",
    protocoloPrefeitura: "PRT-2026-000129",
    codigoVerificacao: "LG129",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0007.pdf",
        anexadoEm: "2026-06-03",
        responsavel: "Financeiro",
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0007.xml",
        anexadoEm: "2026-06-03",
        responsavel: "Financeiro",
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [
      {
        id: 1,
        destino: "financeiro_cliente",
        descricao: "Enviar PDF e XML ao financeiro do cliente.",
        documento: "pdf_xml",
        contato: "financeiro@cliente-logvi.com",
        canal: "email",
        status: "enviado",
        enviadoEm: "2026-06-03",
        responsavel: "Financeiro",
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-06-03",
        titulo: "Nota emitida",
        descricao: "Nota de licenciamento emitida para competência 06/2026.",
        responsavel: "Financeiro",
      },
    ],
  },
];

const competenciasDisponiveis = Array.from(
  new Set(fixedNotas.map((nota) => nota.competencia))
).sort((a, b) => b.localeCompare(a));

const DEFAULT_COMPETENCIA = competenciasDisponiveis[0] || "";

const initialFilters: FilterState = {
  busca: "",
  situacaoFiscal: "",
  statusEnvioCliente: "",
  statusVinculoRecebivel: "",
  tipoReceitaSimples: "",
  cliente: "",
  competencia: DEFAULT_COMPETENCIA,
};

const INITIAL_NOVA_NFSE_FORM: NovaNfseForm = {
  ambiente: "homologacao",

  clienteNome: "",
  clienteDocumento: "",
  clienteEmail: "",

  municipioPrestacao: "Niterói",
  ufPrestacao: "RJ",
  competencia: DEFAULT_COMPETENCIA || getCompetenciaAtualFallback(),

  codigoServico: "14.01",
  codigoCnae: "",
  descricaoServico: "",
  tipoReceitaSimples: "reparo_tecnico",

  valorServico: "",
  aliquotaIss: "2.00",

  issRetido: false,
  simplesNacional: true,
  incentivoFiscal: false,

  projeto: "",
  centroCusto: "Financeiro",
  observacao: "",
};

function normalizeTextSearch(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function onlyNumbers(value: string) {
  return value.replace(/\D/g, "");
}

function parseMoneyInput(value: string) {
  const normalized = value
    .replace(/\./g, "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : 0;
}

function getCompetenciaAtualFallback() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
}

function formatCurrency(value: number | null) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
}

function formatPercent(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(value: string | null) {
  if (!value) return "Não informado";

  const [year, month, day] = value.split("-");

  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

function formatCompetencia(value: string) {
  const [year, month] = value.split("-");

  if (!year || !month) return value;

  return `${month}/${year}`;
}

function getTipoNotaLabel(tipo: TipoNotaFiscal) {
  switch (tipo) {
    case "servico":
      return "Serviço";
    case "produto":
      return "Produto";
    case "mista":
      return "Mista";
    default:
      return "Indefinido";
  }
}

function getTipoReceitaSimplesLabel(tipo: TipoReceitaSimples) {
  switch (tipo) {
    case "reparo_tecnico":
      return "Reparo técnico";
    case "licenciamento_software":
      return "Licenciamento de software";
    case "outro_servico":
      return "Outro serviço";
    default:
      return "Indefinido";
  }
}

function getAliquotaSimples(tipo: TipoReceitaSimples) {
  switch (tipo) {
    case "reparo_tecnico":
      return 0.06;
    case "licenciamento_software":
      return 0.14;
    case "outro_servico":
      return 0.06;
    default:
      return 0;
  }
}

function isNotaSemValorFiscal(nota: NotaFiscalEmitida) {
  return (
    nota.situacaoFiscal === "rascunho" ||
    nota.situacaoFiscal === "cancelada" ||
    nota.situacaoFiscal === "substituida" ||
    nota.situacaoFiscal === "rejeitada_homologacao" ||
    nota.situacaoFiscal === "erro_integracao"
  );
}

function getValorSimples(nota: NotaFiscalEmitida) {
  if (isNotaSemValorFiscal(nota)) return 0;

  return nota.valorBruto * getAliquotaSimples(nota.tipoReceitaSimples);
}

function getValorLiquidoPrevisto(nota: NotaFiscalEmitida) {
  if (isNotaSemValorFiscal(nota)) return 0;

  return nota.valorBruto - getValorSimples(nota);
}

function getSituacaoFiscalLabel(status: SituacaoFiscal) {
  switch (status) {
    case "rascunho":
      return "Rascunho";
    case "regular":
      return "Regular";
    case "aguardando_pdf":
      return "Aguardando PDF";
    case "aguardando_xml":
      return "Aguardando XML";
    case "inconsistente":
      return "Inconsistente";
    case "cancelada":
      return "Cancelada";
    case "substituida":
      return "Substituída";
    case "enviada_homologacao":
      return "Enviada homologação";
    case "autorizada_homologacao":
      return "Autorizada homologação";
    case "rejeitada_homologacao":
      return "Rejeitada homologação";
    case "erro_integracao":
      return "Erro integração";
    default:
      return "Indefinido";
  }
}

function getStatusEnvioClienteLabel(status: StatusEnvioCliente) {
  switch (status) {
    case "nao_iniciado":
      return "Não enviado";
    case "parcial":
      return "Envio parcial";
    case "concluido":
      return "Envio concluído";
    case "pendente_validacao":
      return "Pendente validação";
    case "nao_aplicavel":
      return "Não aplicável";
    default:
      return "Indefinido";
  }
}

function getStatusRecebivelLabel(status: StatusVinculoRecebivel) {
  switch (status) {
    case "sem_recebivel":
      return "Sem recebível";
    case "recebivel_aberto":
      return "A receber";
    case "recebivel_vencido":
      return "Vencido";
    case "recebivel_recebido":
      return "Recebido";
    default:
      return "Indefinido";
  }
}

function getDestinoLabel(destino: DestinoEnvio) {
  switch (destino) {
    case "solicitante":
      return "Solicitante";
    case "fiscal_cliente":
      return "Fiscal do cliente";
    case "compras_cliente":
      return "Compras do cliente";
    case "portal_cliente":
      return "Portal do cliente";
    case "financeiro_cliente":
      return "Financeiro do cliente";
    default:
      return "Destino";
  }
}

function getDocumentoEnvioLabel(documento: ProcedimentoEnvioItem["documento"]) {
  switch (documento) {
    case "pdf":
      return "PDF";
    case "xml":
      return "XML";
    case "pdf_xml":
      return "PDF + XML";
    case "outro":
      return "Outro";
    default:
      return "Documento";
  }
}

function getCanalLabel(canal: ProcedimentoEnvioItem["canal"]) {
  switch (canal) {
    case "email":
      return "E-mail";
    case "portal":
      return "Portal";
    case "whatsapp":
      return "WhatsApp";
    case "outro":
      return "Outro";
    default:
      return "Canal";
  }
}

function getSituacaoFiscalVisual(status: SituacaoFiscal) {
  switch (status) {
    case "regular":
    case "autorizada_homologacao":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <ShieldCheck size={12} />,
      };

    case "rascunho":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };

    case "aguardando_pdf":
    case "aguardando_xml":
    case "enviada_homologacao":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };

    case "inconsistente":
    case "cancelada":
    case "substituida":
    case "rejeitada_homologacao":
    case "erro_integracao":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <ShieldAlert size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getEnvioClienteVisual(status: StatusEnvioCliente) {
  switch (status) {
    case "concluido":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <MailCheck size={12} />,
      };

    case "parcial":
    case "pendente_validacao":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <MailQuestion size={12} />,
      };

    case "nao_iniciado":
      return {
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
        icon: <Send size={12} />,
      };

    case "nao_aplicavel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getRecebivelVisual(status: StatusVinculoRecebivel) {
  switch (status) {
    case "recebivel_recebido":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };

    case "recebivel_aberto":
      return {
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
        icon: <WalletCards size={12} />,
      };

    case "recebivel_vencido":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };

    case "sem_recebivel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Link2 size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getDocumentoStatusLabel(status: StatusDocumentoFiscal) {
  switch (status) {
    case "anexado":
      return "Anexado";
    case "pendente":
      return "Pendente";
    case "nao_aplicavel":
      return "Não aplicável";
    default:
      return "Indefinido";
  }
}

function getDocumentoStatusVisual(status: StatusDocumentoFiscal) {
  switch (status) {
    case "anexado":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };

    case "pendente":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };

    case "nao_aplicavel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getItemEnvioVisual(status: StatusItemEnvio) {
  switch (status) {
    case "enviado":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };

    case "pendente":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };

    case "nao_aplicavel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getItemEnvioLabel(status: StatusItemEnvio) {
  switch (status) {
    case "enviado":
      return "Enviado";
    case "pendente":
      return "Pendente";
    case "nao_aplicavel":
      return "Não aplicável";
    default:
      return "Indefinido";
  }
}

function getDocumento(nota: NotaFiscalEmitida, tipo: TipoDocumentoFiscal) {
  return nota.documentos.find((documento) => documento.tipo === tipo) || null;
}

function getDocumentosObrigatorios(nota: NotaFiscalEmitida) {
  if (nota.situacaoFiscal === "rascunho") {
    return [];
  }

  return DOCUMENTOS_FISCAIS.filter((required) => {
    if (required.tipo === "cancelamento") {
      return nota.situacaoFiscal === "cancelada";
    }

    if (required.tipo === "substituicao") {
      return nota.situacaoFiscal === "substituida";
    }

    return required.tipo === "pdf" || required.tipo === "xml";
  });
}

function getPendenciasDocumentais(nota: NotaFiscalEmitida) {
  return getDocumentosObrigatorios(nota).filter((required) => {
    const documento = getDocumento(nota, required.tipo);

    if (!documento) return true;

    return documento.status === "pendente";
  });
}

function getPendenciasEnvio(nota: NotaFiscalEmitida) {
  if (nota.statusEnvioCliente === "nao_aplicavel") {
    return [];
  }

  return nota.procedimentoEnvio.filter((item) => item.status === "pendente");
}

function getResumoPdfXml(nota: NotaFiscalEmitida) {
  if (nota.situacaoFiscal === "rascunho") {
    return "Não emitida";
  }

  const pdf = getDocumento(nota, "pdf");
  const xml = getDocumento(nota, "xml");

  const pdfOk = pdf?.status === "anexado";
  const xmlOk = xml?.status === "anexado";

  if (pdfOk && xmlOk) return "PDF/XML OK";
  if (pdfOk && !xmlOk) return "Sem XML";
  if (!pdfOk && xmlOk) return "Sem PDF";

  return "PDF/XML pendente";
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "pendencias":
      return "Pendências";
    case "enviadas":
      return "Enviadas";
    case "aguardando-envio":
      return "Aguardando envio";
    case "canceladas":
      return "Canceladas";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}

const Badge: React.FC<{
  children: React.ReactNode;
  background: string;
  color: string;
  border: string;
  icon?: React.ReactNode;
}> = ({ children, background, color, border, icon }) => {
  return (
    <span
      style={{
        ...styles.badge,
        background,
        color,
        borderColor: border,
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: string | number;
  helper: string;
  tone: "neutral" | "success" | "warning" | "danger";
  icon: React.ReactNode;
}> = ({ label, value, helper, tone, icon }) => {
  const iconStyle =
    tone === "success"
      ? styles.summaryIconOk
      : tone === "warning"
      ? styles.summaryIconWarning
      : tone === "danger"
      ? styles.summaryIconDanger
      : styles.summaryIcon;

  return (
    <div style={styles.summaryCard}>
      <div style={iconStyle}>{icon}</div>

      <div style={styles.summaryInfo}>
        <span style={styles.summaryLabel}>{label}</span>
        <strong style={styles.summaryValue}>{value}</strong>
        <span style={styles.summaryHelper}>{helper}</span>
      </div>
    </div>
  );
};

const NotaFiscalRow: React.FC<{
  nota: NotaFiscalEmitida;
  departmentColor: string;
  onOpen: (nota: NotaFiscalEmitida) => void;
}> = ({ nota, departmentColor, onOpen }) => {
  const [hovered, setHovered] = useState(false);

  const fiscalVisual = getSituacaoFiscalVisual(nota.situacaoFiscal);
  const envioVisual = getEnvioClienteVisual(nota.statusEnvioCliente);
  const recebivelVisual = getRecebivelVisual(nota.statusVinculoRecebivel);

  const aliquota = getAliquotaSimples(nota.tipoReceitaSimples);
  const valorImposto = getValorSimples(nota);
  const valorLiquido = getValorLiquidoPrevisto(nota);

  return (
    <tr
      style={{
        ...styles.tr,
        background: hovered ? toRgba(departmentColor, 0.05) : "#ffffff",
        boxShadow: hovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
      }}
      title="Duplo clique para abrir o detalhamento"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => onOpen(nota)}
    >
      <td style={styles.td}>
        <div style={styles.nfCell}>
          <div
            style={{
              ...styles.nfIcon,
              color: hovered ? departmentColor : "#475569",
              borderColor: hovered ? toRgba(departmentColor, 0.3) : "#dbe4ef",
              background: hovered ? toRgba(departmentColor, 0.07) : "#f8fafc",
            }}
          >
            <ReceiptText size={15} />
          </div>

          <div style={styles.nfInfo}>
            <strong
              style={{
                ...styles.nfNumber,
                color: hovered ? departmentColor : "#111827",
              }}
            >
              {nota.numeroNota}
            </strong>

            <span style={styles.mutedText}>{nota.serie}</span>
            <span style={styles.smallMeta}>{getResumoPdfXml(nota)}</span>
          </div>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{nota.cliente}</div>
        <div style={styles.mutedText}>{nota.cnpjCpfCliente}</div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{formatDate(nota.dataEmissao)}</div>
        <div style={styles.mutedText}>
          Competência {formatCompetencia(nota.competencia)}
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.valueText}>{formatCurrency(nota.valorBruto)}</div>

        <div style={styles.taxInlineText}>
          Imposto {formatCurrency(valorImposto)} ({formatPercent(aliquota)})
        </div>

        <div style={styles.mutedText}>
          Líquido {formatCurrency(valorLiquido)}
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>
          {getTipoReceitaSimplesLabel(nota.tipoReceitaSimples)}
        </div>
        <div style={styles.mutedText}>
          {nota.codigoServico} · {nota.projeto}
        </div>
      </td>

      <td style={styles.td}>
        <Badge
          background={fiscalVisual.background}
          color={fiscalVisual.color}
          border={fiscalVisual.border}
          icon={fiscalVisual.icon}
        >
          {getSituacaoFiscalLabel(nota.situacaoFiscal)}
        </Badge>
      </td>

      <td style={styles.td}>
        <Badge
          background={envioVisual.background}
          color={envioVisual.color}
          border={envioVisual.border}
          icon={envioVisual.icon}
        >
          {getStatusEnvioClienteLabel(nota.statusEnvioCliente)}
        </Badge>
      </td>

      <td style={styles.tdNoRightBorder}>
        <Badge
          background={recebivelVisual.background}
          color={recebivelVisual.color}
          border={recebivelVisual.border}
          icon={recebivelVisual.icon}
        >
          {getStatusRecebivelLabel(nota.statusVinculoRecebivel)}
        </Badge>
      </td>
    </tr>
  );
};

function useViewportWidth() {
  const [width, setWidth] = React.useState<number>(() =>
    typeof window === "undefined" ? 1280 : window.innerWidth
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => {
      setWidth(window.innerWidth);
    };

    update();

    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("resize", update);
    };
  }, []);

  return width;
}

const NotaFiscalDetailOverlay: React.FC<{
  nota: NotaFiscalEmitida | null;
  departmentColor: string;
  onClose: () => void;
}> = ({ nota, departmentColor, onClose }) => {
  const viewportWidth = useViewportWidth();
  const isMobileViewport = viewportWidth < MOBILE_BREAKPOINT;

  React.useEffect(() => {
    if (!nota) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [nota, onClose]);

  if (!nota) return null;

  const overlayBackdropStyle: React.CSSProperties = {
    ...styles.overlayBackdrop,
    top: isMobileViewport ? 0 : HEADER_HEIGHT,
    left: isMobileViewport ? 0 : LEFTBAR_WIDTH,
    zIndex: isMobileViewport ? 1400 : 1190,
  };

  const overlayPanelStyle: React.CSSProperties = {
    ...styles.overlayPanel,
    width: isMobileViewport
      ? "100vw"
      : `min(980px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: isMobileViewport ? "100vh" : `calc(100vh - ${HEADER_HEIGHT}px)`,
  };

  const fiscalVisual = getSituacaoFiscalVisual(nota.situacaoFiscal);
  const envioVisual = getEnvioClienteVisual(nota.statusEnvioCliente);
  const recebivelVisual = getRecebivelVisual(nota.statusVinculoRecebivel);

  const aliquotaSimples = getAliquotaSimples(nota.tipoReceitaSimples);
  const valorImposto = getValorSimples(nota);
  const valorLiquido = getValorLiquidoPrevisto(nota);

  return (
    <div
      style={overlayBackdropStyle}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={overlayPanelStyle}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>
              Financeiro / Notas fiscais emitidas
            </div>

            <h2 style={styles.overlayTitle}>
              NF {nota.numeroNota} · {nota.cliente}
            </h2>

            <p style={styles.overlaySubtitle}>
              Dados fiscais, documentos, envio ao cliente, vínculo financeiro e
              histórico da nota.
            </p>
          </div>

          <button
            type="button"
            style={styles.overlayCloseButton}
            onClick={onClose}
            aria-label="Fechar detalhamento"
          >
            <X size={17} />
          </button>
        </div>

        <div style={styles.detailContent}>
          <section style={styles.detailTopGrid}>
            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Valor bruto da NF</span>
              <strong style={styles.detailValue}>
                {formatCurrency(nota.valorBruto)}
              </strong>
              <span style={styles.detailMeta}>Base de cálculo estimada</span>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Imposto estimado</span>
              <strong style={styles.detailValue}>
                {formatCurrency(valorImposto)}
              </strong>
              <span style={styles.detailMeta}>
                Simples Nacional {formatPercent(aliquotaSimples)}
              </span>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Valor líquido previsto</span>
              <strong style={styles.detailValue}>
                {formatCurrency(valorLiquido)}
              </strong>
              <span style={styles.detailMeta}>
                Bruto menos imposto estimado
              </span>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Conta a receber</span>

              <div style={styles.detailBadgeArea}>
                <Badge
                  background={recebivelVisual.background}
                  color={recebivelVisual.color}
                  border={recebivelVisual.border}
                  icon={recebivelVisual.icon}
                >
                  {getStatusRecebivelLabel(nota.statusVinculoRecebivel)}
                </Badge>
              </div>
            </div>
          </section>

          <section style={styles.detailGridTwo}>
            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <ReceiptText size={15} />
                <strong>Dados da nota</strong>
              </div>

              <div style={styles.infoGridTwo}>
                <div style={styles.infoItem}>
                  <span>Número / Série</span>
                  <strong>
                    {nota.numeroNota} · {nota.serie}
                  </strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Tipo</span>
                  <strong>{getTipoNotaLabel(nota.tipo)}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Emissão</span>
                  <strong>{formatDate(nota.dataEmissao)}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Competência</span>
                  <strong>{formatCompetencia(nota.competencia)}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Município</span>
                  <strong>
                    {nota.municipioPrestacao}/{nota.ufPrestacao}
                  </strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Responsável</span>
                  <strong>{nota.responsavel}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Protocolo prefeitura</span>
                  <strong>{nota.protocoloPrefeitura || "Não informado"}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Código verificação</span>
                  <strong>{nota.codigoVerificacao || "Não informado"}</strong>
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <FileText size={15} />
                <strong>Tomador</strong>
              </div>

              <div style={styles.infoGridOne}>
                <div style={styles.infoItem}>
                  <span>Cliente</span>
                  <strong>{nota.cliente}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>CNPJ / CPF</span>
                  <strong>{nota.cnpjCpfCliente}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Projeto / Case</span>
                  <strong>{nota.projeto}</strong>
                </div>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <FileCheck2 size={15} />
              <strong>Serviço, tributação e valores</strong>
            </div>

            <div style={styles.serviceBox}>
              <div style={styles.serviceDescription}>
                <span>Serviço / Receita</span>

                <strong>
                  {nota.codigoServico} ·{" "}
                  {getTipoReceitaSimplesLabel(nota.tipoReceitaSimples)}
                </strong>

                <p>{nota.descricaoServico}</p>
              </div>

              <div style={styles.taxGrid}>
                <div style={styles.taxItem}>
                  <span>Valor bruto</span>
                  <strong>{formatCurrency(nota.valorBruto)}</strong>
                </div>

                <div style={styles.taxItem}>
                  <span>Alíquota</span>
                  <strong>{formatPercent(aliquotaSimples)}</strong>
                </div>

                <div style={styles.taxItem}>
                  <span>Imposto estimado</span>
                  <strong>{formatCurrency(valorImposto)}</strong>
                </div>

                <div style={styles.taxItem}>
                  <span>Regime</span>
                  <strong>Simples Nacional</strong>
                </div>

                <div style={styles.taxItemWide}>
                  <span>Valor líquido previsto</span>
                  <strong>{formatCurrency(valorLiquido)}</strong>
                </div>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <ShieldCheck size={15} />
              <strong>Situação fiscal e envio</strong>
            </div>

            <div style={styles.statusGrid}>
              <div style={styles.statusCard}>
                <span>Situação fiscal</span>

                <Badge
                  background={fiscalVisual.background}
                  color={fiscalVisual.color}
                  border={fiscalVisual.border}
                  icon={fiscalVisual.icon}
                >
                  {getSituacaoFiscalLabel(nota.situacaoFiscal)}
                </Badge>
              </div>

              <div style={styles.statusCard}>
                <span>Envio ao cliente</span>

                <Badge
                  background={envioVisual.background}
                  color={envioVisual.color}
                  border={envioVisual.border}
                  icon={envioVisual.icon}
                >
                  {getStatusEnvioClienteLabel(nota.statusEnvioCliente)}
                </Badge>
              </div>

              <div style={styles.statusCard}>
                <span>Recebível</span>

                <Badge
                  background={recebivelVisual.background}
                  color={recebivelVisual.color}
                  border={recebivelVisual.border}
                  icon={recebivelVisual.icon}
                >
                  {getStatusRecebivelLabel(nota.statusVinculoRecebivel)}
                </Badge>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <Paperclip size={15} />
              <strong>Documentos fiscais</strong>
            </div>

            <div style={styles.documentsGrid}>
              {DOCUMENTOS_FISCAIS.map((required) => {
                const documento = getDocumento(nota, required.tipo);
                const status = documento?.status || "pendente";
                const visual = getDocumentoStatusVisual(status);

                return (
                  <div key={required.tipo} style={styles.documentCard}>
                    <div style={styles.documentHeader}>
                      <div style={styles.documentTitleArea}>
                        <strong>{required.label}</strong>

                        <span>
                          {documento?.arquivoNome || "Nenhum arquivo anexado"}
                        </span>
                      </div>

                      <Badge
                        background={visual.background}
                        color={visual.color}
                        border={visual.border}
                        icon={visual.icon}
                      >
                        {getDocumentoStatusLabel(status)}
                      </Badge>
                    </div>

                    <div style={styles.documentFooter}>
                      <span>
                        Anexado em {formatDate(documento?.anexadoEm || null)}
                      </span>

                      <div style={styles.documentActions}>
                        <button
                          type="button"
                          style={{
                            ...styles.iconButton,
                            color:
                              status === "anexado"
                                ? departmentColor
                                : "#94a3b8",
                            borderColor:
                              status === "anexado"
                                ? toRgba(departmentColor, 0.35)
                                : "#e2e8f0",
                            cursor:
                              status === "anexado" ? "pointer" : "not-allowed",
                          }}
                          disabled={status !== "anexado"}
                        >
                          <Eye size={13} />
                        </button>

                        <button
                          type="button"
                          style={{
                            ...styles.iconButton,
                            color:
                              status === "anexado"
                                ? departmentColor
                                : "#94a3b8",
                            borderColor:
                              status === "anexado"
                                ? toRgba(departmentColor, 0.35)
                                : "#e2e8f0",
                            cursor:
                              status === "anexado" ? "pointer" : "not-allowed",
                          }}
                          disabled={status !== "anexado"}
                        >
                          <Download size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <MailCheck size={15} />
              <strong>Envio ao cliente</strong>
            </div>

            <div style={styles.envioGrid}>
              {nota.procedimentoEnvio.length === 0 && (
                <div style={styles.emptyEnvio}>
                  Nenhum procedimento de envio aplicável para esta nota.
                </div>
              )}

              {nota.procedimentoEnvio.map((item) => {
                const visual = getItemEnvioVisual(item.status);

                return (
                  <div key={item.id} style={styles.envioCard}>
                    <div style={styles.envioCardHeader}>
                      <div style={styles.envioTitleArea}>
                        <strong>{getDestinoLabel(item.destino)}</strong>
                        <span>{item.descricao}</span>
                      </div>

                      <Badge
                        background={visual.background}
                        color={visual.color}
                        border={visual.border}
                        icon={visual.icon}
                      >
                        {getItemEnvioLabel(item.status)}
                      </Badge>
                    </div>

                    <div style={styles.envioMetaGrid}>
                      <div>
                        <span>Documento</span>
                        <strong>
                          {getDocumentoEnvioLabel(item.documento)}
                        </strong>
                      </div>

                      <div>
                        <span>Canal</span>
                        <strong>{getCanalLabel(item.canal)}</strong>
                      </div>

                      <div>
                        <span>Contato / Portal</span>
                        <strong>{item.contato}</strong>
                      </div>

                      <div>
                        <span>Enviado em</span>
                        <strong>{formatDate(item.enviadoEm)}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section style={styles.detailGridTwo}>
            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <WalletCards size={15} />
                <strong>Vínculo financeiro</strong>
              </div>

              <div style={styles.infoGridOne}>
                <div style={styles.infoItem}>
                  <span>Conta a receber</span>
                  <strong>{nota.contaReceberCodigo || "Não vinculada"}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Vencimento</span>
                  <strong>{formatDate(nota.contaReceberVencimento)}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Valor líquido previsto</span>
                  <strong>{formatCurrency(valorLiquido)}</strong>
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <Clock3 size={15} />
                <strong>Histórico</strong>
              </div>

              <div style={styles.historyList}>
                {nota.historico.map((item) => (
                  <div key={item.id} style={styles.historyItem}>
                    <div style={styles.historyDot} />

                    <div>
                      <strong>{item.titulo}</strong>

                      <span>
                        {formatDate(item.data)} · {item.responsavel}
                      </span>

                      <p>{item.descricao}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {nota.observacao && (
            <section style={styles.observationBox}>
              <strong>Observação interna</strong>
              <span>{nota.observacao}</span>
            </section>
          )}
        </div>

        <div style={styles.overlayFooter}>
          <button type="button" style={styles.cancelButton} onClick={onClose}>
            Fechar
          </button>

          <button type="button" style={styles.secondaryButton}>
            <Send size={14} />
            Registrar envio
          </button>

          <button type="button" style={styles.secondaryButton}>
            <Link2 size={14} />
            Vincular recebível
          </button>

          <button type="button" style={styles.secondaryDangerButton}>
            <RefreshCcw size={14} />
            Cancelar/Substituir
          </button>

          <button
            type="button"
            style={{
              ...styles.primaryButton,
              background: departmentColor,
              borderColor: departmentColor,
            }}
          >
            <Upload size={14} />
            Anexar PDF/XML
          </button>
        </div>
      </div>
    </div>
  );
};

const NovaNfsePrefeituraOverlay: React.FC<{
  open: boolean;
  form: NovaNfseForm;
  departmentColor: string;
  loading: boolean;
  error: string | null;
  success: string | null;
  onClose: () => void;
  onChange: <K extends keyof NovaNfseForm>(
    key: K,
    value: NovaNfseForm[K]
  ) => void;
  onSubmit: () => void;
  onFillFakeData: () => void;
}> = ({
  open,
  form,
  departmentColor,
  loading,
  error,
  success,
  onClose,
  onChange,
  onSubmit,
  onFillFakeData,
}) => {
  const viewportWidth = useViewportWidth();
  const isMobileViewport = viewportWidth < MOBILE_BREAKPOINT;

  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !loading) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, loading, onClose]);

  if (!open) return null;

  const overlayBackdropStyle: React.CSSProperties = {
    ...styles.overlayBackdrop,
    top: isMobileViewport ? 0 : HEADER_HEIGHT,
    left: isMobileViewport ? 0 : LEFTBAR_WIDTH,
    zIndex: isMobileViewport ? 1400 : 1190,
  };

  const overlayPanelStyle: React.CSSProperties = {
    ...styles.overlayPanel,
    width: isMobileViewport
      ? "100vw"
      : `min(980px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: isMobileViewport ? "100vh" : `calc(100vh - ${HEADER_HEIGHT}px)`,
  };

  const valorServico = parseMoneyInput(form.valorServico);
  const aliquotaIss = Number(String(form.aliquotaIss).replace(",", ".")) || 0;
  const valorIss = valorServico > 0 ? (valorServico * aliquotaIss) / 100 : 0;

  return (
    <div
      style={overlayBackdropStyle}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div
        style={overlayPanelStyle}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>
              Financeiro / Gestão fiscal / Nova NFS-e
            </div>

            <h2 style={styles.overlayTitle}>
              Emitir NFS-e em homologação da prefeitura
            </h2>

            <p style={styles.overlaySubtitle}>
              Emissão de teste no ambiente oficial de homologação municipal,
              usando o certificado digital e-CNPJ A1 configurado no backend.
            </p>
          </div>

          <button
            type="button"
            style={styles.overlayCloseButton}
            onClick={onClose}
            aria-label="Fechar emissão de NFS-e"
            disabled={loading}
          >
            <X size={17} />
          </button>
        </div>

        <div style={styles.detailContent}>
          <section
            style={{
              ...styles.detailSection,
              borderColor: toRgba(departmentColor, 0.24),
              background: toRgba(departmentColor, 0.035),
            }}
          >
            <div style={styles.detailSectionHeader}>
              <ShieldCheck size={15} />
              <strong>Ambiente fiscal</strong>
            </div>

            <div style={styles.infoGridTwo}>
              <div style={styles.infoItem}>
                <span>Ambiente</span>
                <strong>Homologação da prefeitura</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Certificado</span>
                <strong>e-CNPJ A1 no backend</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Produção</span>
                <strong>Bloqueada neste endpoint</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Tipo</span>
                <strong>NFS-e / Serviço</strong>
              </div>
            </div>

            <div style={styles.fiscalNoticeBox}>
              <strong>Regra de segurança</strong>
              <span>
                O certificado A1, senha, endpoint municipal e assinatura XML
                ficam exclusivamente no backend. O front envia apenas os dados
                da nota.
              </span>
            </div>
          </section>

          {error && (
            <section style={styles.errorBox}>
              <strong>Erro na emissão</strong>
              <span>{error}</span>
            </section>
          )}

          {success && (
            <section style={styles.successBox}>
              <strong>Retorno da prefeitura</strong>
              <span>{success}</span>
            </section>
          )}

          <section style={styles.detailGridTwo}>
            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <FileText size={15} />
                <strong>Tomador do serviço</strong>
              </div>

              <div style={styles.formGridOne}>
                <label style={styles.formField}>
                  <span>Cliente / Razão social</span>
                  <input
                    style={styles.formInput}
                    value={form.clienteNome}
                    onChange={(event) =>
                      onChange("clienteNome", event.target.value)
                    }
                    placeholder="Ex.: Cliente Teste Homologação Ltda"
                  />
                </label>

                <label style={styles.formField}>
                  <span>CNPJ / CPF</span>
                  <input
                    style={styles.formInput}
                    value={form.clienteDocumento}
                    onChange={(event) =>
                      onChange("clienteDocumento", event.target.value)
                    }
                    placeholder="Somente números ou formatado"
                  />
                </label>

                <label style={styles.formField}>
                  <span>E-mail fiscal / financeiro</span>
                  <input
                    style={styles.formInput}
                    value={form.clienteEmail}
                    onChange={(event) =>
                      onChange("clienteEmail", event.target.value)
                    }
                    placeholder="fiscal@cliente.com.br"
                  />
                </label>
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <ReceiptText size={15} />
                <strong>Dados municipais</strong>
              </div>

              <div style={styles.formGridTwo}>
                <label style={styles.formField}>
                  <span>Município da prestação</span>
                  <input
                    style={styles.formInput}
                    value={form.municipioPrestacao}
                    onChange={(event) =>
                      onChange("municipioPrestacao", event.target.value)
                    }
                    placeholder="Niterói"
                  />
                </label>

                <label style={styles.formField}>
                  <span>UF</span>
                  <input
                    style={styles.formInput}
                    value={form.ufPrestacao}
                    onChange={(event) =>
                      onChange("ufPrestacao", event.target.value.toUpperCase())
                    }
                    maxLength={2}
                    placeholder="RJ"
                  />
                </label>

                <label style={styles.formField}>
                  <span>Competência</span>
                  <input
                    style={styles.formInput}
                    type="month"
                    value={form.competencia}
                    onChange={(event) =>
                      onChange("competencia", event.target.value)
                    }
                  />
                </label>

                <label style={styles.formField}>
                  <span>Tipo de receita</span>
                  <select
                    style={styles.formInput}
                    value={form.tipoReceitaSimples}
                    onChange={(event) =>
                      onChange(
                        "tipoReceitaSimples",
                        event.target.value as TipoReceitaSimples
                      )
                    }
                  >
                    <option value="reparo_tecnico">Reparo técnico</option>
                    <option value="licenciamento_software">
                      Licenciamento de software
                    </option>
                    <option value="outro_servico">Outro serviço</option>
                  </select>
                </label>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <FileCheck2 size={15} />
              <strong>Serviço e tributação</strong>
            </div>

            <div style={styles.formGridTwo}>
              <label style={styles.formField}>
                <span>Código de serviço municipal</span>
                <input
                  style={styles.formInput}
                  value={form.codigoServico}
                  onChange={(event) =>
                    onChange("codigoServico", event.target.value)
                  }
                  placeholder="14.01"
                />
              </label>

              <label style={styles.formField}>
                <span>CNAE</span>
                <input
                  style={styles.formInput}
                  value={form.codigoCnae}
                  onChange={(event) =>
                    onChange("codigoCnae", onlyNumbers(event.target.value))
                  }
                  placeholder="Opcional"
                />
              </label>

              <label style={styles.formField}>
                <span>Valor do serviço</span>
                <input
                  style={styles.formInput}
                  value={form.valorServico}
                  onChange={(event) =>
                    onChange("valorServico", event.target.value)
                  }
                  placeholder="Ex.: 12500,00"
                />
              </label>

              <label style={styles.formField}>
                <span>Alíquota ISS (%)</span>
                <input
                  style={styles.formInput}
                  value={form.aliquotaIss}
                  onChange={(event) =>
                    onChange("aliquotaIss", event.target.value)
                  }
                  placeholder="2.00"
                />
              </label>
            </div>

            <label style={styles.formField}>
              <span>Descrição do serviço</span>
              <textarea
                style={styles.formTextarea}
                value={form.descricaoServico}
                onChange={(event) =>
                  onChange("descricaoServico", event.target.value)
                }
                placeholder="Descreva o serviço prestado conforme exigência fiscal da prefeitura."
              />
            </label>

            <div style={styles.checkboxGrid}>
              <label style={styles.checkboxItem}>
                <input
                  type="checkbox"
                  checked={form.issRetido}
                  onChange={(event) =>
                    onChange("issRetido", event.target.checked)
                  }
                />
                <span>ISS retido pelo tomador</span>
              </label>

              <label style={styles.checkboxItem}>
                <input
                  type="checkbox"
                  checked={form.simplesNacional}
                  onChange={(event) =>
                    onChange("simplesNacional", event.target.checked)
                  }
                />
                <span>Optante pelo Simples Nacional</span>
              </label>

              <label style={styles.checkboxItem}>
                <input
                  type="checkbox"
                  checked={form.incentivoFiscal}
                  onChange={(event) =>
                    onChange("incentivoFiscal", event.target.checked)
                  }
                />
                <span>Incentivo fiscal</span>
              </label>
            </div>

            <div style={styles.taxPreviewGrid}>
              <div style={styles.taxItem}>
                <span>Valor bruto</span>
                <strong>{formatCurrency(valorServico)}</strong>
              </div>

              <div style={styles.taxItem}>
                <span>ISS estimado</span>
                <strong>{formatCurrency(valorIss)}</strong>
              </div>

              <div style={styles.taxItem}>
                <span>Ambiente</span>
                <strong>Homologação</strong>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <Link2 size={15} />
              <strong>Referência interna</strong>
            </div>

            <div style={styles.formGridTwo}>
              <label style={styles.formField}>
                <span>Projeto / Case</span>
                <input
                  style={styles.formInput}
                  value={form.projeto}
                  onChange={(event) => onChange("projeto", event.target.value)}
                  placeholder="Ex.: Case HVX-2026-001"
                />
              </label>

              <label style={styles.formField}>
                <span>Centro de custo</span>
                <input
                  style={styles.formInput}
                  value={form.centroCusto}
                  onChange={(event) =>
                    onChange("centroCusto", event.target.value)
                  }
                  placeholder="Financeiro"
                />
              </label>
            </div>

            <label style={styles.formField}>
              <span>Observação interna</span>
              <textarea
                style={styles.formTextareaSmall}
                value={form.observacao}
                onChange={(event) => onChange("observacao", event.target.value)}
                placeholder="Pedido de compra, BM, item do pedido, observações fiscais etc."
              />
            </label>
          </section>
        </div>

        <div style={styles.overlayFooter}>
          <button
            type="button"
            style={styles.cancelButton}
            onClick={onClose}
            disabled={loading}
          >
            Fechar
          </button>

          <button
            type="button"
            style={{
              ...styles.secondaryButton,
              opacity: loading ? 0.65 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
            onClick={onFillFakeData}
            disabled={loading}
          >
            <FileCheck2 size={14} />
            Preencher dados fictícios
          </button>

          <button
            type="button"
            style={{
              ...styles.primaryButton,
              background: departmentColor,
              borderColor: departmentColor,
              opacity: loading ? 0.75 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
            onClick={onSubmit}
            disabled={loading}
          >
            <Send size={14} />
            {loading ? "Enviando..." : "Emitir em homologação"}
          </button>
        </div>
      </div>
    </div>
  );
};

const NotasFiscaisEmitidasLayout: React.FC<NotasFiscaisEmitidasLayoutProps> = ({
  section,
}) => {
  const { departmentColor } = useTheme();

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;
  const activeSection = section || "visao-geral";

  const [notas, setNotas] = useState<NotaFiscalEmitida[]>(fixedNotas);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [selectedNota, setSelectedNota] = useState<NotaFiscalEmitida | null>(
    null
  );

  const [novaNfseOpen, setNovaNfseOpen] = useState(false);
  const [novaNfseForm, setNovaNfseForm] = useState<NovaNfseForm>({
    ...INITIAL_NOVA_NFSE_FORM,
    competencia: DEFAULT_COMPETENCIA || getCompetenciaAtualFallback(),
  });
  const [novaNfseLoading, setNovaNfseLoading] = useState(false);
  const [novaNfseError, setNovaNfseError] = useState<string | null>(null);
  const [novaNfseSuccess, setNovaNfseSuccess] = useState<string | null>(null);

  const selectedCompetencia = filters.competencia || DEFAULT_COMPETENCIA;

  const competenciasAtuais = useMemo(() => {
    const competencias = Array.from(
      new Set([
        ...competenciasDisponiveis,
        ...notas.map((nota) => nota.competencia),
        getCompetenciaAtualFallback(),
      ])
    );

    return competencias.sort((a, b) => b.localeCompare(a));
  }, [notas]);

  const clientes = useMemo(() => {
    return Array.from(
      new Set(
        notas
          .filter((nota) =>
            selectedCompetencia
              ? nota.competencia === selectedCompetencia
              : true
          )
          .map((nota) => nota.cliente)
      )
    ).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [notas, selectedCompetencia]);

  const filteredByMonth = useMemo(() => {
    if (!selectedCompetencia) return notas;

    return notas.filter((nota) => nota.competencia === selectedCompetencia);
  }, [notas, selectedCompetencia]);

  const filteredBySection = useMemo(() => {
    switch (activeSection) {
      case "pendencias":
        return filteredByMonth.filter(
          (nota) =>
            nota.situacaoFiscal !== "regular" ||
            getPendenciasDocumentais(nota).length > 0 ||
            getPendenciasEnvio(nota).length > 0
        );

      case "enviadas":
        return filteredByMonth.filter(
          (nota) => nota.statusEnvioCliente === "concluido"
        );

      case "aguardando-envio":
        return filteredByMonth.filter(
          (nota) =>
            nota.statusEnvioCliente === "nao_iniciado" ||
            nota.statusEnvioCliente === "parcial" ||
            nota.statusEnvioCliente === "pendente_validacao"
        );

      case "canceladas":
        return filteredByMonth.filter(
          (nota) =>
            nota.situacaoFiscal === "cancelada" ||
            nota.situacaoFiscal === "substituida"
        );

      case "visao-geral":
      default:
        return filteredByMonth;
    }
  }, [activeSection, filteredByMonth]);

  const filteredItems = useMemo(() => {
    const busca = normalizeTextSearch(filters.busca);

    return filteredBySection.filter((nota) => {
      const buscaMatch =
        !busca ||
        [
          nota.numeroNota,
          nota.serie,
          nota.cliente,
          nota.cnpjCpfCliente,
          nota.municipioPrestacao,
          nota.ufPrestacao,
          nota.codigoServico,
          nota.descricaoServico,
          nota.centroCusto,
          nota.projeto,
          nota.responsavel,
          nota.contaReceberCodigo,
          nota.protocoloPrefeitura,
          nota.codigoVerificacao,
          getTipoReceitaSimplesLabel(nota.tipoReceitaSimples),
        ].some((field) => normalizeTextSearch(field).includes(busca));

      const fiscalMatch =
        !filters.situacaoFiscal ||
        nota.situacaoFiscal === filters.situacaoFiscal;

      const envioMatch =
        !filters.statusEnvioCliente ||
        nota.statusEnvioCliente === filters.statusEnvioCliente;

      const recebivelMatch =
        !filters.statusVinculoRecebivel ||
        nota.statusVinculoRecebivel === filters.statusVinculoRecebivel;

      const tipoReceitaMatch =
        !filters.tipoReceitaSimples ||
        nota.tipoReceitaSimples === filters.tipoReceitaSimples;

      const clienteMatch = !filters.cliente || nota.cliente === filters.cliente;

      return (
        buscaMatch &&
        fiscalMatch &&
        envioMatch &&
        recebivelMatch &&
        tipoReceitaMatch &&
        clienteMatch
      );
    });
  }, [filteredBySection, filters]);

  const resumo = useMemo(() => {
    const notasValidasMes = filteredByMonth.filter(
      (nota) =>
        nota.situacaoFiscal !== "rascunho" &&
        nota.situacaoFiscal !== "cancelada" &&
        nota.situacaoFiscal !== "substituida" &&
        nota.situacaoFiscal !== "rejeitada_homologacao" &&
        nota.situacaoFiscal !== "erro_integracao"
    );

    const valorFaturado = notasValidasMes.reduce(
      (sum, nota) => sum + nota.valorBruto,
      0
    );

    const impostoEstimado = notasValidasMes.reduce(
      (sum, nota) => sum + getValorSimples(nota),
      0
    );

    const valorLiquidoPrevisto = notasValidasMes.reduce(
      (sum, nota) => sum + getValorLiquidoPrevisto(nota),
      0
    );

    const pendenciasEnvio = filteredByMonth.filter(
      (nota) => getPendenciasEnvio(nota).length > 0
    ).length;

    const canceladasSubstituidas = filteredByMonth.filter(
      (nota) =>
        nota.situacaoFiscal === "cancelada" ||
        nota.situacaoFiscal === "substituida"
    ).length;

    return {
      emitidasMes: notasValidasMes.length,
      valorFaturado,
      impostoEstimado,
      valorLiquidoPrevisto,
      pendenciasEnvio,
      canceladasSubstituidas,
    };
  }, [filteredByMonth]);

  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      ...initialFilters,
      competencia: selectedCompetencia || DEFAULT_COMPETENCIA,
    });
  };

  const updateNovaNfseForm = <K extends keyof NovaNfseForm>(
    key: K,
    value: NovaNfseForm[K]
  ) => {
    setNovaNfseForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const openNovaNfse = () => {
    setNovaNfseError(null);
    setNovaNfseSuccess(null);

    setNovaNfseForm((prev) => ({
      ...prev,
      ambiente: "homologacao",
      competencia:
        filters.competencia ||
        DEFAULT_COMPETENCIA ||
        getCompetenciaAtualFallback(),
    }));

    setNovaNfseOpen(true);
  };

  const closeNovaNfse = () => {
    if (novaNfseLoading) return;
    setNovaNfseOpen(false);
  };

  const preencherDadosFicticiosHomologacao = () => {
    if (novaNfseLoading) return;

    const now = new Date();
    const competenciaAtual = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}`;
    const sufixoTeste = String(now.getTime()).slice(-5);

    setNovaNfseError(null);
    setNovaNfseSuccess(null);

    setNovaNfseForm({
      ambiente: "homologacao",

      clienteNome: `Cliente Teste Homologação ${sufixoTeste} Ltda`,
      clienteDocumento: "11.222.333/0001-81",
      clienteEmail: `fiscal.teste.${sufixoTeste}@exemplo.com.br`,

      municipioPrestacao: "Niterói",
      ufPrestacao: "RJ",
      competencia:
        filters.competencia || DEFAULT_COMPETENCIA || competenciaAtual,

      codigoServico: "14.01",
      codigoCnae: "3314710",
      descricaoServico:
        "Serviço fictício de manutenção, diagnóstico técnico, testes funcionais e validação operacional de equipamento eletrônico marítimo. Dados utilizados exclusivamente para teste em ambiente de homologação.",
      tipoReceitaSimples: "reparo_tecnico",

      valorServico: "1.250,00",
      aliquotaIss: "2.00",

      issRetido: false,
      simplesNacional: true,
      incentivoFiscal: false,

      projeto: `HML-NFSE-${sufixoTeste}`,
      centroCusto: "Financeiro / Homologação",
      observacao:
        "Dados fictícios gerados automaticamente pelo botão de teste. Não utilizar em ambiente de produção.",
    });
  };

  const validateNovaNfseForm = () => {
    if (!novaNfseForm.clienteNome.trim()) {
      return "Informe o nome ou razão social do tomador.";
    }

    if (!novaNfseForm.descricaoServico.trim()) {
      return "Informe a descrição do serviço.";
    }

    if (parseMoneyInput(novaNfseForm.valorServico) <= 0) {
      return "Informe um valor de serviço maior que zero.";
    }

    if (!novaNfseForm.codigoServico.trim()) {
      return "Informe o código de serviço municipal.";
    }

    if (!novaNfseForm.municipioPrestacao.trim()) {
      return "Informe o município da prestação.";
    }

    if (!novaNfseForm.ufPrestacao.trim()) {
      return "Informe a UF da prestação.";
    }

    return null;
  };

  const emitirNovaNfseHomologacaoPrefeitura = async () => {
    const validationError = validateNovaNfseForm();

    if (validationError) {
      setNovaNfseError(validationError);
      setNovaNfseSuccess(null);
      return;
    }

    setNovaNfseLoading(true);
    setNovaNfseError(null);
    setNovaNfseSuccess(null);

    try {
      const payload = {
        ambiente: "homologacao",

        empresa_id: 3,
        endereco_id: 6,
        departamento_id: 34,
        modulo_id: 91,

        tomador: {
          nome: novaNfseForm.clienteNome.trim(),
          documento: onlyNumbers(novaNfseForm.clienteDocumento),
          email: novaNfseForm.clienteEmail.trim() || null,
        },

        prestacao: {
          municipio: novaNfseForm.municipioPrestacao.trim(),
          uf: novaNfseForm.ufPrestacao.trim().toUpperCase(),
          competencia: novaNfseForm.competencia,
        },

        servico: {
          codigo_servico_municipal: novaNfseForm.codigoServico.trim(),
          codigo_cnae: novaNfseForm.codigoCnae.trim() || null,
          descricao: novaNfseForm.descricaoServico.trim(),
          valor_servico: parseMoneyInput(novaNfseForm.valorServico),
          aliquota_iss:
            Number(String(novaNfseForm.aliquotaIss).replace(",", ".")) || 0,
          iss_retido: novaNfseForm.issRetido,
          simples_nacional: novaNfseForm.simplesNacional,
          incentivo_fiscal: novaNfseForm.incentivoFiscal,
          tipo_receita_simples: novaNfseForm.tipoReceitaSimples,
        },

        interno: {
          projeto: novaNfseForm.projeto.trim() || null,
          centro_custo: novaNfseForm.centroCusto.trim() || null,
          observacao: novaNfseForm.observacao.trim() || null,
        },
      };

      const response = await fetch(
        "https://api.logvi-havox.com/template/financeiro/nfse/prefeitura/emitir-homologacao",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = (await response.json()) as EmitirNfsePrefeituraResponse;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.message ||
            data?.nota?.mensagem_retorno ||
            "Não foi possível emitir a NFS-e em homologação da prefeitura."
        );
      }

      const novaNota: NotaFiscalEmitida = {
        id: Number(data.nota?.id || Date.now()),
        numeroNota: data.nota?.numero_nfse || "HOMOLOGAÇÃO",
        serie: "NFS-e",
        tipo: "servico",
        tipoReceitaSimples: novaNfseForm.tipoReceitaSimples,
        cliente: novaNfseForm.clienteNome.trim(),
        cnpjCpfCliente: novaNfseForm.clienteDocumento.trim() || "Não informado",
        municipioPrestacao: novaNfseForm.municipioPrestacao.trim(),
        ufPrestacao: novaNfseForm.ufPrestacao.trim().toUpperCase(),
        competencia: novaNfseForm.competencia,
        dataEmissao: new Date().toISOString().slice(0, 10),
        valorBruto: parseMoneyInput(novaNfseForm.valorServico),
        situacaoFiscal:
          data.nota?.status === "autorizada_homologacao"
            ? "autorizada_homologacao"
            : "enviada_homologacao",
        statusEnvioCliente: "nao_iniciado",
        statusVinculoRecebivel: "sem_recebivel",
        contaReceberCodigo: null,
        contaReceberVencimento: null,
        codigoServico: novaNfseForm.codigoServico.trim(),
        descricaoServico: novaNfseForm.descricaoServico.trim(),
        centroCusto: novaNfseForm.centroCusto.trim() || "Financeiro",
        projeto: novaNfseForm.projeto.trim() || "NFS-e homologação",
        responsavel: "Financeiro",
        observacao:
          novaNfseForm.observacao.trim() ||
          "NFS-e emitida em ambiente de homologação da prefeitura.",
        protocoloPrefeitura: data.nota?.protocolo_prefeitura || null,
        codigoVerificacao: data.nota?.codigo_verificacao || null,
        documentos: [
          {
            tipo: "pdf",
            label: "PDF da nota",
            status: "pendente",
            arquivoNome: null,
            anexadoEm: null,
            responsavel: null,
          },
          {
            tipo: "xml",
            label: "XML da nota",
            status: data.nota?.xml_retorno ? "anexado" : "pendente",
            arquivoNome: data.nota?.xml_retorno
              ? `NFSE-HML-${data.nota?.id || Date.now()}.xml`
              : null,
            anexadoEm: data.nota?.xml_retorno
              ? new Date().toISOString().slice(0, 10)
              : null,
            responsavel: data.nota?.xml_retorno ? "Financeiro" : null,
          },
          {
            tipo: "cancelamento",
            label: "Cancelamento",
            status: "nao_aplicavel",
            arquivoNome: null,
            anexadoEm: null,
            responsavel: null,
          },
          {
            tipo: "substituicao",
            label: "Substituição",
            status: "nao_aplicavel",
            arquivoNome: null,
            anexadoEm: null,
            responsavel: null,
          },
        ],
        procedimentoEnvio: [],
        historico: [
          {
            id: 1,
            data: new Date().toISOString().slice(0, 10),
            titulo: "NFS-e enviada para homologação",
            descricao:
              data.nota?.mensagem_retorno ||
              data.message ||
              "Nota enviada ao ambiente de homologação da prefeitura.",
            responsavel: "Financeiro",
          },
        ],
      };

      setNotas((prev) => [novaNota, ...prev]);

      setNovaNfseSuccess(
        data.nota?.protocolo_prefeitura
          ? `NFS-e enviada para homologação da prefeitura. Protocolo: ${data.nota.protocolo_prefeitura}.`
          : data.message || "NFS-e enviada para homologação da prefeitura."
      );
    } catch (error) {
      setNovaNfseError(
        error instanceof Error
          ? error.message
          : "Erro inesperado ao emitir NFS-e em homologação da prefeitura."
      );
    } finally {
      setNovaNfseLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <TitleButtons fullBleedLine title={`Notas fiscais emitidas`} />

      <div style={styles.content}>
        <section style={styles.monthPanel}>
          <div style={styles.monthInfo}>
            <strong>Apuração mensal</strong>
          </div>

          <div style={styles.monthControl}>
            <span>Competência</span>

            <select
              style={styles.monthSelect}
              value={selectedCompetencia}
              onChange={(event) =>
                updateFilter("competencia", event.target.value)
              }
            >
              {competenciasAtuais.map((competencia) => (
                <option key={competencia} value={competencia}>
                  {formatCompetencia(competencia)}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section style={styles.summaryGrid}>
          <SummaryCard
            label="Emitidas no mês"
            value={resumo.emitidasMes}
            helper={`${resumo.canceladasSubstituidas} cancelada(s) ou substituída(s)`}
            tone="neutral"
            icon={<ReceiptText size={16} />}
          />

          <SummaryCard
            label="Valor bruto faturado"
            value={formatCurrency(resumo.valorFaturado)}
            helper={`Total bruto válido em ${formatCompetencia(
              selectedCompetencia
            )}`}
            tone="success"
            icon={<WalletCards size={16} />}
          />

          <SummaryCard
            label="Imposto estimado"
            value={formatCurrency(resumo.impostoEstimado)}
            helper="6% reparo técnico / 14% licenciamento"
            tone="warning"
            icon={<FileCheck2 size={16} />}
          />

          <SummaryCard
            label="Líquido previsto"
            value={formatCurrency(resumo.valorLiquidoPrevisto)}
            helper={`${resumo.pendenciasEnvio} pendência(s) de envio no mês`}
            tone="neutral"
            icon={<CheckCircle2 size={16} />}
          />
        </section>

        <section style={styles.actionBar}>
          <div style={styles.actionBarInfo}>
            <strong>Controle fiscal e operacional de notas emitidas</strong>
          </div>

          <div style={styles.actionButtons}>
            <button
              type="button"
              style={{
                ...styles.primaryButton,
                background: activeDepartmentColor,
                borderColor: activeDepartmentColor,
              }}
              onClick={openNovaNfse}
            >
              <Plus size={14} />
              Nova nota
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Upload size={14} />
              Importar XML
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Paperclip size={14} />
              Anexar PDF/XML
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Link2 size={14} />
              Vincular recebível
            </button>
          </div>
        </section>

        <section style={styles.filterPanel}>
          <div style={styles.filterHeader}>
            <div style={styles.filterTitleArea}>
              <Filter size={15} />
              <strong>Filtros da listagem</strong>
            </div>

            <button
              type="button"
              style={styles.clearButton}
              onClick={clearFilters}
            >
              Limpar filtros
            </button>
          </div>

          <div style={styles.filterGrid}>
            <div style={styles.searchField}>
              <Search size={14} />

              <input
                style={styles.searchInput}
                value={filters.busca}
                onChange={(event) => updateFilter("busca", event.target.value)}
                placeholder="Buscar por NF, cliente, CNPJ, serviço, projeto, protocolo ou recebível..."
              />
            </div>

            <select
              style={styles.filterInput}
              value={filters.situacaoFiscal}
              onChange={(event) =>
                updateFilter("situacaoFiscal", event.target.value)
              }
            >
              <option value="">Todas as situações fiscais</option>
              <option value="rascunho">Rascunho</option>
              <option value="regular">Regular</option>
              <option value="aguardando_pdf">Aguardando PDF</option>
              <option value="aguardando_xml">Aguardando XML</option>
              <option value="inconsistente">Inconsistente</option>
              <option value="cancelada">Cancelada</option>
              <option value="substituida">Substituída</option>
              <option value="enviada_homologacao">Enviada homologação</option>
              <option value="autorizada_homologacao">
                Autorizada homologação
              </option>
              <option value="rejeitada_homologacao">
                Rejeitada homologação
              </option>
              <option value="erro_integracao">Erro integração</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.statusEnvioCliente}
              onChange={(event) =>
                updateFilter("statusEnvioCliente", event.target.value)
              }
            >
              <option value="">Todos os envios</option>
              <option value="nao_iniciado">Não enviado</option>
              <option value="parcial">Envio parcial</option>
              <option value="concluido">Envio concluído</option>
              <option value="pendente_validacao">Pendente validação</option>
              <option value="nao_aplicavel">Não aplicável</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.statusVinculoRecebivel}
              onChange={(event) =>
                updateFilter("statusVinculoRecebivel", event.target.value)
              }
            >
              <option value="">Todos os vínculos financeiros</option>
              <option value="sem_recebivel">Sem recebível</option>
              <option value="recebivel_aberto">A receber</option>
              <option value="recebivel_vencido">Vencido</option>
              <option value="recebivel_recebido">Recebido</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.tipoReceitaSimples}
              onChange={(event) =>
                updateFilter("tipoReceitaSimples", event.target.value)
              }
            >
              <option value="">Todos os tipos de receita</option>
              <option value="reparo_tecnico">Reparo técnico</option>
              <option value="licenciamento_software">
                Licenciamento de software
              </option>
              <option value="outro_servico">Outro serviço</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.cliente}
              onChange={(event) => updateFilter("cliente", event.target.value)}
            >
              <option value="">Todos os clientes</option>
              {clientes.map((cliente) => (
                <option key={cliente} value={cliente}>
                  {cliente}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section style={styles.tablePanel}>
          <div style={styles.tableHeader}>
            <div>
              <strong>Notas fiscais</strong>
            </div>
          </div>

          <div style={styles.tableScroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>NF</th>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Emissão</th>
                  <th style={styles.th}>Valores</th>
                  <th style={styles.th}>Serviço</th>
                  <th style={styles.th}>Fiscal</th>
                  <th style={styles.th}>Envio</th>
                  <th style={styles.thNoRightBorder}>Recebível</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.map((nota) => (
                  <NotaFiscalRow
                    key={nota.id}
                    nota={nota}
                    departmentColor={activeDepartmentColor}
                    onOpen={setSelectedNota}
                  />
                ))}

                {filteredItems.length === 0 && (
                  <tr>
                    <td style={styles.emptyCell} colSpan={8}>
                      Nenhuma nota encontrada para os filtros selecionados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <NotaFiscalDetailOverlay
        nota={selectedNota}
        departmentColor={activeDepartmentColor}
        onClose={() => setSelectedNota(null)}
      />

      <NovaNfsePrefeituraOverlay
        open={novaNfseOpen}
        form={novaNfseForm}
        departmentColor={activeDepartmentColor}
        loading={novaNfseLoading}
        error={novaNfseError}
        success={novaNfseSuccess}
        onClose={closeNovaNfse}
        onChange={updateNovaNfseForm}
        onSubmit={emitirNovaNfseHomologacaoPrefeitura}
        onFillFakeData={preencherDadosFicticiosHomologacao}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    minHeight: "100vh",
    background: "#FFFFFF",
    color: "#0f172a",
    fontFamily:
      "Nunito, Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },

  content: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  monthPanel: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  monthInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },

  monthControl: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 850,
    color: "#475569",
  },

  monthSelect: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    padding: "0 10px",
    fontSize: 12,
    fontWeight: 800,
    color: "#0f172a",
    outline: "none",
    fontFamily: "inherit",
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  summaryCard: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "12px",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    minHeight: 92,
  },

  summaryIcon: {
    width: 34,
    height: 34,
    border: "1px solid #dbe4ef",
    background: "#f8fafc",
    color: "#475569",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  summaryIconOk: {
    width: 34,
    height: 34,
    border: "1px solid #bbf7d0",
    background: "#ecfdf3",
    color: "#166534",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  summaryIconWarning: {
    width: 34,
    height: 34,
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#9a3412",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  summaryIconDanger: {
    width: 34,
    height: 34,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  summaryInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },

  summaryLabel: {
    fontSize: 11,
    fontWeight: 850,
    color: "#64748b",
  },

  summaryValue: {
    fontSize: 19,
    fontWeight: 900,
    color: "#0f172a",
    lineHeight: 1.1,
  },

  summaryHelper: {
    fontSize: 11,
    fontWeight: 750,
    color: "#64748b",
    lineHeight: 1.3,
  },

  actionBar: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  actionBarInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },

  actionButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },

  filterPanel: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  filterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  filterTitleArea: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 13,
    fontWeight: 900,
    color: "#0f172a",
  },

  clearButton: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    height: 30,
    padding: "0 10px",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  filterGrid: {
    display: "grid",
    gridTemplateColumns: "2fr repeat(5, minmax(150px, 1fr))",
    gap: 8,
  },

  searchField: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "0 10px",
    color: "#64748b",
  },

  searchInput: {
    border: 0,
    outline: 0,
    flex: 1,
    height: "100%",
    fontSize: 12,
    fontWeight: 750,
    color: "#0f172a",
    fontFamily: "inherit",
    minWidth: 0,
  },

  filterInput: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    padding: "0 9px",
    fontSize: 12,
    fontWeight: 750,
    outline: "none",
    fontFamily: "inherit",
    minWidth: 0,
  },

  tablePanel: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    overflow: "hidden",
  },

  tableHeader: {
    padding: "11px 12px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
  },

  tableScroll: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 1180,
  },

  th: {
    padding: "10px 12px",
    textAlign: "left",
    borderRight: "1px solid #e2e8f0",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: 11,
    fontWeight: 900,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.25,
  },

  thNoRightBorder: {
    padding: "10px 12px",
    textAlign: "left",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: 11,
    fontWeight: 900,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.25,
  },

  tr: {
    transition: "background 140ms ease, box-shadow 140ms ease",
    cursor: "default",
  },

  td: {
    padding: "10px 12px",
    borderRight: "1px solid #eef2f7",
    borderBottom: "1px solid #eef2f7",
    verticalAlign: "top",
  },

  tdNoRightBorder: {
    padding: "10px 12px",
    borderBottom: "1px solid #eef2f7",
    verticalAlign: "top",
  },

  emptyCell: {
    padding: "22px",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 800,
    color: "#64748b",
  },

  nfCell: {
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
  },

  nfIcon: {
    width: 32,
    height: 32,
    border: "1px solid #dbe4ef",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  nfInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  nfNumber: {
    fontSize: 13,
    fontWeight: 950,
    lineHeight: 1.1,
  },

  mainText: {
    fontSize: 12,
    fontWeight: 850,
    color: "#0f172a",
    lineHeight: 1.25,
  },

  mutedText: {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    lineHeight: 1.25,
  },

  smallMeta: {
    fontSize: 10,
    fontWeight: 850,
    color: "#64748b",
    lineHeight: 1.2,
  },

  valueText: {
    fontSize: 13,
    fontWeight: 950,
    color: "#0f172a",
    lineHeight: 1.25,
  },

  taxInlineText: {
    fontSize: 11,
    fontWeight: 800,
    color: "#9a3412",
    lineHeight: 1.25,
  },

  badge: {
    minHeight: 24,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  overlayBackdrop: {
    position: "fixed",
    top: HEADER_HEIGHT,
    left: LEFTBAR_WIDTH,
    right: 0,
    bottom: 0,
    zIndex: 1190,
    display: "flex",
    justifyContent: "flex-end",
    background:
      "radial-gradient(circle at top left, rgba(47, 63, 89, 0.10), transparent 34%), rgba(15, 23, 42, 0.28)",
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
    boxSizing: "border-box",
  },

  overlayPanel: {
    width: `min(980px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    background: "#ffffff",
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid #d9e2ec",
    boxShadow: "-18px 0 46px rgba(15, 23, 42, 0.22)",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  overlayHeader: {
    padding: "16px 18px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
  },

  overlayEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },

  overlayTitle: {
    margin: "5px 0 4px",
    fontSize: 20,
    fontWeight: 950,
    color: "#0f172a",
    lineHeight: 1.2,
  },

  overlaySubtitle: {
    margin: 0,
    fontSize: 12,
    fontWeight: 750,
    color: "#64748b",
    lineHeight: 1.45,
    maxWidth: 720,
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },

  detailContent: {
    flex: 1,
    overflowY: "auto",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    background: "#f8fafc",
  },

  detailTopGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  detailGridTwo: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  detailCard: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "11px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },

  detailLabel: {
    fontSize: 11,
    fontWeight: 850,
    color: "#64748b",
  },

  detailValue: {
    fontSize: 17,
    fontWeight: 950,
    color: "#0f172a",
  },

  detailMeta: {
    fontSize: 11,
    fontWeight: 750,
    color: "#64748b",
  },

  detailBadgeArea: {
    marginTop: 5,
  },

  detailSection: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  detailSectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 13,
    fontWeight: 900,
    color: "#0f172a",
  },

  infoGridTwo: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },

  infoGridOne: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
  },

  infoItem: {
    border: "1px solid #eef2f7",
    background: "#f8fafc",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 11,
    color: "#64748b",
    fontWeight: 800,
  },

  serviceBox: {
    display: "grid",
    gridTemplateColumns: "1.3fr 1fr",
    gap: 10,
  },

  serviceDescription: {
    border: "1px solid #eef2f7",
    background: "#f8fafc",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },

  taxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },

  taxItem: {
    border: "1px solid #eef2f7",
    background: "#ffffff",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 11,
    color: "#64748b",
    fontWeight: 800,
  },

  taxItemWide: {
    border: "1px solid #eef2f7",
    background: "#ffffff",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 11,
    color: "#64748b",
    fontWeight: 800,
    gridColumn: "span 2",
  },

  statusGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
  },

  statusCard: {
    border: "1px solid #eef2f7",
    background: "#f8fafc",
    padding: "9px",
    display: "flex",
    flexDirection: "column",
    gap: 7,
    fontSize: 11,
    color: "#64748b",
    fontWeight: 850,
  },

  documentsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },

  documentCard: {
    border: "1px solid #eef2f7",
    background: "#f8fafc",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  documentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },

  documentTitleArea: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 12,
    color: "#0f172a",
  },

  documentFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    fontSize: 11,
    fontWeight: 750,
    color: "#64748b",
  },

  documentActions: {
    display: "flex",
    gap: 6,
  },

  iconButton: {
    width: 28,
    height: 28,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  envioGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
  },

  envioCard: {
    border: "1px solid #eef2f7",
    background: "#f8fafc",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  envioCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },

  envioTitleArea: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 12,
    color: "#0f172a",
  },

  envioMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 8,
    fontSize: 11,
    color: "#64748b",
    fontWeight: 800,
  },

  emptyEnvio: {
    border: "1px dashed #cbd5e1",
    background: "#ffffff",
    padding: "12px",
    fontSize: 12,
    fontWeight: 800,
    color: "#64748b",
  },

  historyList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  historyItem: {
    display: "grid",
    gridTemplateColumns: "10px 1fr",
    gap: 8,
    fontSize: 12,
    color: "#0f172a",
  },

  historyDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: "#94a3b8",
    marginTop: 4,
  },

  observationBox: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 12,
    fontWeight: 750,
    color: "#475569",
  },

  fiscalNoticeBox: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 12,
    fontWeight: 800,
    color: "#334155",
  },

  formGridOne: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 10,
  },

  formGridTwo: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  formField: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    fontSize: 11,
    fontWeight: 850,
    color: "#475569",
  },

  formInput: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    padding: "0 10px",
    fontSize: 12,
    fontWeight: 700,
    outline: "none",
    fontFamily: "inherit",
  },

  formTextarea: {
    minHeight: 92,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    padding: "9px 10px",
    fontSize: 12,
    fontWeight: 700,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: 1.45,
  },

  formTextareaSmall: {
    minHeight: 62,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    padding: "9px 10px",
    fontSize: 12,
    fontWeight: 700,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: 1.45,
  },

  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
    marginTop: 2,
  },

  checkboxItem: {
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: "8px 9px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    fontWeight: 800,
    color: "#334155",
  },

  taxPreviewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
    marginTop: 2,
  },

  errorBox: {
    border: "1px solid #fecaca",
    background: "#fffafa",
    color: "#991b1b",
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 12,
    fontWeight: 750,
  },

  successBox: {
    border: "1px solid #bbf7d0",
    background: "#ecfdf3",
    color: "#166534",
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 12,
    fontWeight: 750,
  },

  overlayFooter: {
    padding: "10px 12px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },

  cancelButton: {
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    padding: "0 12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  primaryButton: {
    height: 32,
    border: "1px solid",
    color: "#ffffff",
    padding: "0 12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  secondaryButton: {
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "0 11px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  secondaryDangerButton: {
    height: 32,
    border: "1px solid #fecaca",
    background: "#fffafa",
    color: "#991b1b",
    padding: "0 11px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },
};

export default NotasFiscaisEmitidasLayout;
